import type { Payload } from 'payload'

import type { Discount } from '@/payload-types'

export type DiscountScope =
  | {
      discount: Discount
      scope: 'all'
      productIds: number[]
      categoryIds: number[]
    }
  | {
      discount: Discount
      scope: 'product'
      productIds: number[]
      categoryIds: number[]
    }
  | {
      discount: Discount
      scope: 'category'
      productIds: number[]
      categoryIds: number[]
    }

type RelationshipValue = (number | { id: number })[] | null | undefined

export function extractIdsFromRelationship(value: RelationshipValue): number[] {
  if (!value || value.length === 0) return []
  return value
    .map((entry) => {
      if (typeof entry === 'number') return entry
      if (typeof entry === 'object' && entry !== null && typeof entry.id === 'number') return entry.id
      return null
    })
    .filter((id): id is number => typeof id === 'number')
}

export async function resolveDiscountScope(
  payload: Payload,
  discountId: number,
): Promise<DiscountScope | null> {
  if (!discountId || Number.isNaN(discountId)) return null

  let discount: Discount | null = null
  try {
    discount = (await payload.findByID({
      collection: 'discounts',
      id: discountId,
      depth: 0,
    })) as Discount
  } catch (err) {
    return null
  }

  if (!discount) return null

  const isActive = discount.is_active ?? true
  if (!isActive) return null

  const now = new Date()
  const startDate = discount.start_date ? new Date(discount.start_date) : null
  const expirationDate = discount.expiration_date ? new Date(discount.expiration_date) : null

  if (startDate && startDate > now) return null
  if (expirationDate && expirationDate < now) return null

  if (discount.appliesTo === 'all') {
    return { discount, scope: 'all', productIds: [], categoryIds: [] }
  }

  if (discount.appliesTo === 'product') {
    const productIds = extractIdsFromRelationship(discount.products)
    if (productIds.length === 0) return null
    return {
      discount,
      scope: 'product',
      productIds,
      categoryIds: [],
    }
  }

  if (discount.appliesTo === 'category') {
    const categoryIds = extractIdsFromRelationship(discount.category)
    if (categoryIds.length === 0) return null
    return {
      discount,
      scope: 'category',
      productIds: [],
      categoryIds,
    }
  }

  return null
}
